import { supabase } from '../lib/supabase'
import { diffLabelAssignments } from '../lib/labelAssignments'

/**
 * Inbox labels (Phase 8.10): tenant-scoped, color-coded tags applied to
 * conversations (many-to-many via conversation_label_assignments).
 *
 * Permissions (enforced by RLS): anyone with communication.inbox can read,
 * create, apply, and remove labels; rename / recolor / delete requires
 * communication.inbox_admin. tenant_id is stamped server-side.
 */

export interface ConversationLabel {
  id: string
  name: string
  color: string
  sort_order: number
}

export interface LabelAssignmentRow {
  conversation_id: string
  label_id: string
}

/** Fixed palette for label colors (picker swatches + inline-create rotation). */
export const LABEL_COLORS = [
  '#dc2626', // red
  '#ea580c', // orange
  '#ca8a04', // yellow
  '#16a34a', // green
  '#0d9488', // teal
  '#2563eb', // blue
  '#7c3aed', // violet
  '#db2777', // pink
  '#6b7280', // gray
]

export async function fetchConversationLabels(): Promise<ConversationLabel[]> {
  const { data, error } = await supabase
    .from('conversation_labels')
    .select('id, name, color, sort_order')
    .order('sort_order', { ascending: true })
    .order('name', { ascending: true })
  if (error) throw error
  return data || []
}

export async function createConversationLabel(
  name: string,
  color: string
): Promise<ConversationLabel> {
  const { data, error } = await supabase
    .from('conversation_labels')
    .insert({ name: name.trim(), color })
    .select('id, name, color, sort_order')
    .single()
  if (error) throw error
  return data
}

export async function updateConversationLabel(
  id: string,
  patch: Partial<Pick<ConversationLabel, 'name' | 'color' | 'sort_order'>>
): Promise<void> {
  const { error } = await supabase.from('conversation_labels').update(patch).eq('id', id)
  if (error) throw error
}

/** Deleting a label cascades its assignments; conversations are untouched. */
export async function deleteConversationLabel(id: string): Promise<void> {
  const { error } = await supabase.from('conversation_labels').delete().eq('id', id)
  if (error) throw error
}

export async function fetchLabelAssignments(): Promise<LabelAssignmentRow[]> {
  const { data, error } = await supabase
    .from('conversation_label_assignments')
    .select('conversation_id, label_id')
  if (error) throw error
  return data || []
}

export async function addLabelToConversation(
  conversationId: string,
  labelId: string
): Promise<void> {
  const { error } = await supabase
    .from('conversation_label_assignments')
    .insert({ conversation_id: conversationId, label_id: labelId })
  // 23505 = already assigned (e.g. two tabs), which is fine
  if (error && error.code !== '23505') throw error
}

export async function removeLabelFromConversation(
  conversationId: string,
  labelId: string
): Promise<void> {
  const { error } = await supabase
    .from('conversation_label_assignments')
    .delete()
    .eq('conversation_id', conversationId)
    .eq('label_id', labelId)
  if (error) throw error
}

/** Replace a conversation's labels with `nextLabelIds` using minimal inserts/deletes. */
export async function setConversationLabels(
  conversationId: string,
  currentLabelIds: string[],
  nextLabelIds: string[]
): Promise<void> {
  const { toAdd, toRemove } = diffLabelAssignments(currentLabelIds, nextLabelIds)
  if (toAdd.length > 0) {
    const { error } = await supabase
      .from('conversation_label_assignments')
      .insert(toAdd.map((labelId) => ({ conversation_id: conversationId, label_id: labelId })))
    if (error && error.code !== '23505') throw error
  }
  if (toRemove.length > 0) {
    const { error } = await supabase
      .from('conversation_label_assignments')
      .delete()
      .eq('conversation_id', conversationId)
      .in('label_id', toRemove)
    if (error) throw error
  }
}
